import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import StoreX, { store } from '../../redux/oldStore';
import { ServerResponse } from '../../utils/Server';
import DangerousInnerHtml from '../ParticipantVideo/DangerousInnerHtml';
import './VirtualRooms.css';

interface ICategoryRoom {
  id: number;
  name: string;
  roomId: string;
}

interface ICircleRoom {
  circleId: number;
  name: string;
  roundId: number;
  roomId: string;
}

interface IRound {
  id: number;
  name: string;
}

interface IProjectRoom {
  roomId: string;
  projectId?: string;
  title?: string;
}

interface IVirtualRoomsData {
  fairId: string;
  fairName?: string;
  isStaff: boolean;
  isJudge: boolean;
  isStudent: boolean;
  staffRoomId: string;
  showStaffRoom: boolean;
  showCategoryRooms: boolean;
  categories: ICategoryRoom[];
  useCircles: boolean;
  rounds: IRound[];
  circles: ICircleRoom[];
  projectRoom?: IProjectRoom | null;
  participantInstructions?: string | null;
  settings?: {
    useJitsi?: boolean;
    onlineJudgingVirtualInterviews?: boolean;
    onlineJudgingVirtualInterviewsForCircles?: boolean;
  };
}

const fairParam = () => {
  const f = StoreX.GetQueryStringValue('f') || StoreX.Settings?.fairId;
  return f ? `f=${f}` : '';
};

const roomHref = (roomId: string) => {
  const qs = fairParam();
  // Match existing video links elsewhere (AdvancedJudging, JudgingDashboard): raw room id in path.
  return qs ? `/app/video/${roomId}?${qs}` : `/app/video/${roomId}`;
};

/**
 * Virtual meeting room directory (replaces Hinge/_index_hinge).
 * Links into existing /app/video/:roomId conference UI.
 */
const VirtualRoomsPage = () => {
  const [data, setData] = useState<IVirtualRoomsData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    store.server
      .postApi<ServerResponse<IVirtualRoomsData>>('../Hinge/Rooms', {})
      .then((x) => {
        if (x.Success && x.Value) {
          setData(x.Value);
        } else {
          toast.error(x.Message || 'Unable to load virtual rooms.', { autoClose: false });
        }
      })
      .catch(() => toast.error('Unable to load virtual rooms.', { autoClose: false }))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="main-body-page grow virtual-rooms-page" data-testid="virtual-rooms-page">
        <p className="text-muted">Loading meeting rooms…</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="main-body-page grow virtual-rooms-page" data-testid="virtual-rooms-page">
        <div className="alert alert-warning">Virtual rooms are not available.</div>
      </div>
    );
  }

  const rules = (
    <div className="virtual-rooms-rules alert alert-info">
      <h4>
        <i className="fa fa-exclamation-triangle text-danger" /> Virtual Judging and Interview Rules
      </h4>
      <p>By using this service you agree to all of the rules of this contest and the following guidelines.</p>
      <ul>
        <li>Do not share where you live.</li>
        <li>Do not give out your contact information, including phone numbers, address, and email.</li>
        <li>Do not share private or sensitive information.</li>
        <li>Conduct yourself with the highest levels of professionalism.</li>
      </ul>
    </div>
  );

  return (
    <div className="main-body-page grow virtual-rooms-page" data-testid="virtual-rooms-page">
      <header className="virtual-rooms-header">
        <div>
          <h2>
            <i className="fad fa-video" /> Meeting Rooms
          </h2>
          <p className="text-muted virtual-rooms-subtitle">
            Join staff, category, or interview rooms for {data.fairName || 'this event'}.
          </p>
        </div>
      </header>

      {data.showStaffRoom && (
        <section className="virtual-rooms-section">
          <h3>Staff</h3>
          <div className="virtual-rooms-grid">
            <a className="virtual-room-card virtual-room-card--staff" href={roomHref(data.staffRoomId)} target="_blank" rel="noreferrer">
              <i className="fad fa-users-crown" />
              <span className="virtual-room-card__title">Staff Room</span>
              <span className="virtual-room-card__meta">Default staff meeting</span>
            </a>
          </div>
        </section>
      )}

      {data.showCategoryRooms && (
        <section className="virtual-rooms-section">
          <h3>Judge Category Rooms</h3>
          <p className="virtual-rooms-help">
            Need a place to talk with the other judges of a category? Use your category room.
          </p>
          {!data.settings?.onlineJudgingVirtualInterviewsForCircles && (
            <div className="alert alert-info">
              <strong>
                <i className="fa fa-exclamation-triangle text-danger" /> Judges:
              </strong>{' '}
              access interview rooms via online judging when available.
            </div>
          )}
          {data.categories?.length ? (
            <div className="virtual-rooms-grid">
              {data.categories.map((c) => (
                <a key={c.id} className="virtual-room-card" href={roomHref(c.roomId)} target="_blank" rel="noreferrer">
                  <i className="fad fa-users-class" />
                  <span className="virtual-room-card__title">{c.name}</span>
                  <span className="virtual-room-card__meta">Category room</span>
                </a>
              ))}
            </div>
          ) : (
            <div className="alert alert-default">No categories are configured for this event.</div>
          )}
        </section>
      )}

      {data.useCircles && (
        <section className="virtual-rooms-section">
          {rules}
          {data.participantInstructions && (
            <div className="virtual-rooms-instructions">
              <DangerousInnerHtml customText={data.participantInstructions} />
            </div>
          )}
          {data.circles?.length ? (
            data.rounds.map((round) => {
              const rooms = data.circles.filter((c) => c.roundId === round.id);
              if (!rooms.length) return null;
              return (
                <div key={round.id} className="virtual-rooms-round">
                  <h4>Round: {round.name}</h4>
                  <div className="alert alert-info">
                    <strong>
                      <i className="fa fa-exclamation-triangle text-danger" /> Works best in Chrome or Firefox.
                    </strong>{' '}
                    Click a meeting room to join.
                  </div>
                  <div className="virtual-rooms-grid">
                    {rooms.map((c) => (
                      <a
                        key={c.circleId}
                        className="virtual-room-card"
                        href={roomHref(c.roomId)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <i className="fad fa-users-class" />
                        <span className="virtual-room-card__title">{c.name}</span>
                        <span className="virtual-room-card__meta">Meeting room</span>
                      </a>
                    ))}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="alert alert-info">
              <h4>
                <i className="fa fa-exclamation-triangle text-danger" /> No Assignment
              </h4>
              It appears you have not been assigned to a judging circle at this time.
            </div>
          )}
        </section>
      )}

      {!data.useCircles && data.projectRoom && (
        <section className="virtual-rooms-section">
          {rules}
          {data.participantInstructions && (
            <div className="virtual-rooms-instructions">
              <DangerousInnerHtml customText={data.participantInstructions} />
            </div>
          )}
          <div className="virtual-rooms-grid">
            <a
              className="virtual-room-card virtual-room-card--primary"
              href={roomHref(data.projectRoom.roomId)}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fad fa-person-booth" />
              <span className="virtual-room-card__title">Enter Judging / Interview Room</span>
              <span className="virtual-room-card__meta">
                {[data.projectRoom.projectId, data.projectRoom.title].filter(Boolean).join(' — ')}
              </span>
            </a>
          </div>
        </section>
      )}
    </div>
  );
};

export default VirtualRoomsPage;
